import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useMemo, useRef, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { ArticleDraft } from "@domain/lms/article/article";
import { LightArticle } from "@domain/lms/article/dtos";
import { useEffectAsync, useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { ArticleDetailsEditor } from "./articleDetailsEditor";
import { ArticleEditorController } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/articleEditorController";
import { EditorControllerContext } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/editorControllerContext";
import { ArticleMainEditor } from "./articleMainEditor";
import { PublishArticleModal } from "./publishArticleModal";

interface ArticleEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialArticle: ArticleDraft;
	step: "MAIN" | "DETAILS";
	onStepChange: (step: "MAIN" | "DETAILS") => void;
	articleId?: string;
}
export const ArticleEditor: React.FC<ArticleEditorProps> = ({
	initialArticle,
	articleId,
	step,
	onStepChange,
	...htmlProps
}) => {
	const controller = useMemo(() => new ArticleEditorController(initialArticle), []);

	const article = useObservable(controller.article);
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { createModal } = useModalTools();
	const previousStatus = useRef(article.status);
	const { articleService, trackingService } = useServices();
	const isMobile = useIsMobile();

	const saveArticle = async (
		track?: "track",
		toast?: "toast",
		redirect?: "article" | "editor"
	): Promise<LightArticle | undefined> => {
		setLoading(true);
		const currentArticle = controller.article.get();

		try {
			if (track) {
				trackingService.track("article / save", { status: currentArticle.status });
			}
			const fetchedArticle = await articleService.saveArticle(currentArticle, articleId, currentArticle.publisher?.id);
			if (toast) {
				createToast(I18n.t("article.toast.save.title"), I18n.t("article.toast.save.message"), "success");
			}
			if (redirect === "article") {
				// must redirect to article show
				history.push(
					Path.ReferenceArticle({
						id: fetchedArticle.id,
						slug: fetchedArticle.slug,
						category: fetchedArticle.category,
					})
				);
			}
			return fetchedArticle;
		} catch (e) {
			console.error(e);
			createToast(I18n.t("article.toast.save.title"), I18n.t("article.toast.save.error"), "error");
			return;
		} finally {
			setLoading(false);
		}
	};

	useEffectAsync(async () => {
		if (previousStatus.current !== article.status) {
			const { confirmed, shouldShare, shareMessage } =
				article.status === "PUBLISHED"
					? await new Promise<{ confirmed: boolean; shouldShare: boolean; shareMessage?: ContentState }>((resolve) =>
							createModal((close) => (
								<PublishArticleModal
									articleTitle={article.title}
									onConfirm={(confirmed, shouldShare, shareMessage) => {
										close();
										resolve({
											confirmed,
											shouldShare,
											shareMessage,
										});
									}}
								/>
							))
						)
					: {
							confirmed: true,
							shouldShare: false,
							shareMessage: undefined,
						};
			if (!confirmed) {
				controller.setStatus(previousStatus.current);
				return;
			}

			trackingService.track(`article / ${article.status === "PUBLISHED" ? "publish" : "archive"}`);
			const savedArticle = await saveArticle();
			if (savedArticle) {
				createToast(
					I18n.t("toast.success"),
					I18n.t(`article.toast.${article.status === "PUBLISHED" ? "publish" : "archive"}.message`),
					"success"
				);
				if (shouldShare) {
					await articleService.shareArticle(savedArticle.id, shareMessage);
					trackingService.track("article / share from publish");
				}
				previousStatus.current = article.status;
				// must redirect to article show or edit
				history.push(
					Path.ReferenceArticle({
						id: savedArticle.id,
						slug: savedArticle.slug,
						category: savedArticle.category,
					})
				);
			} else {
				controller.setStatus(previousStatus.current);
			}
		}
	}, [article.status]);

	return (
		<EditorControllerContext.Provider value={controller}>
			<Container {...htmlProps}>
				{isMobile ? (
					step === "MAIN" ? (
						<>
							<ArticleMainEditor />
							<PrimaryButton onClick={() => onStepChange("DETAILS")} disabled={!controller.isValidForNextStep()}>
								{I18n.t("next")}
							</PrimaryButton>
						</>
					) : (
						<ArticleDetailsEditor onSaveArticle={saveArticle} loading={loading} articleId={articleId} />
					)
				) : (
					<>
						<StyledMainEditor />
						<StyledArticleDetailsEditor onSaveArticle={saveArticle} loading={loading} articleId={articleId} />
					</>
				)}
			</Container>
		</EditorControllerContext.Provider>
	);
};

const Container = styled.div`
	width: 100%;
	@media ${desktop} {
		display: grid;
		grid-gap: 20px;
		grid-template-columns: auto 348px;
		grid-template-rows: auto;
		grid-template-areas:
			"main details"
			"main details"
			"main details";
	}
	@media ${mobile} {
		${gap(40)};
		> button {
			margin: 0 auto;
		}
	}
`;

const StyledMainEditor = styled(ArticleMainEditor)`
	grid-area: main;
`;

const StyledArticleDetailsEditor = styled(ArticleDetailsEditor)`
	grid-area: details;
`;
